/**
 * SF-4762 — Platform feature flags page.
 *
 * Lists every platform flag (global per environment; a flag never set is
 * off) with its switch, exempt list, latest change and full history. The
 * first flag is "Staff must sign in with Google", so it can be switched off
 * at once if Google sign-in has a problem.
 *
 * Access mirrors sf-api: admin:platform:flags:read to see the page,
 * admin:platform:flags:write to change. sf-api also requires an unlimited
 * super admin for writes, so staff with a company list only view,
 * like the Eva platform defaults page. The UI gate is defence in depth only.
 */
import { usePermissions } from '@refinedev/core';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Result, Skeleton, Space, Spin, Tag, Typography } from 'antd';
import { hasPermission } from '../../utils/permissions';
import { useCanEditCompany } from '../companies/api/editableCompaniesClient';
import {
  PLATFORM_FLAG_HISTORY_QUERY_KEY,
  PLATFORM_FLAGS_READ_PERMISSION,
  PLATFORM_FLAGS_WRITE_PERMISSION,
  usePlatformFlags,
} from './api/platformFlagsClient';
import { FlagCard } from './FlagCard';

const { Title, Text } = Typography;

export const PlatformFlagsPage: React.FC = () => {
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const perms = permissions ?? [];
  const canRead = hasPermission(perms, PLATFORM_FLAGS_READ_PERMISSION);
  const hasWritePerm = hasPermission(perms, PLATFORM_FLAGS_WRITE_PERMISSION);
  const { canEditPlatform, isLoading: scopeLoading } = useCanEditCompany(undefined);
  const canWrite = hasWritePerm && canEditPlatform;
  // No "you can't" note while the company scope still loads (switches just stay locked).
  const readOnlyReason =
    canWrite || (hasWritePerm && scopeLoading)
      ? null
      : !hasWritePerm
        ? 'You can view platform flags but not change them (admin:platform:flags:write).'
        : 'Platform flags affect every company, so only staff without a company list can change them.';

  const flagsQuery = usePlatformFlags({ enabled: !permsLoading && canRead });
  const flags = flagsQuery.data?.data ?? [];
  const queryClient = useQueryClient();
  const historyFetching = useIsFetching({ queryKey: PLATFORM_FLAG_HISTORY_QUERY_KEY }) > 0;
  // Refresh reloads the list and every card's recent changes, so the page is all from one moment.
  const refresh = () => {
    void flagsQuery.refetch();
    void queryClient.refetchQueries({ queryKey: PLATFORM_FLAG_HISTORY_QUERY_KEY });
  };

  if (permsLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }
  if (!canRead) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="Platform feature flags need the admin:platform:flags:read permission."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Space align="center">
          <Title level={3} style={{ marginBottom: 0 }}>
            Platform feature flags
          </Title>
          <Tag color="purple">Super-admins</Tag>
        </Space>
        <div>
          <Text type="secondary">
            Switches that apply to the whole platform in this environment. A flag that was never set
            is off. Every change is recorded.
          </Text>
        </div>
        {readOnlyReason && <Text type="secondary">{readOnlyReason}</Text>}
        {flagsQuery.data && (
          // The page never reloads by itself, so show when it last did and let the viewer reload.
          <Space size={8}>
            <Text type="secondary">
              Last loaded {new Date(flagsQuery.dataUpdatedAt).toLocaleTimeString()}
            </Text>
            <Button
              size="small"
              loading={flagsQuery.isFetching || historyFetching}
              onClick={refresh}
            >
              Refresh
            </Button>
          </Space>
        )}
      </div>

      {flagsQuery.isLoading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      ) : flagsQuery.isError && !flagsQuery.data ? (
        // Only when nothing ever loaded: a failed reload after a save keeps the
        // cards (with the saved value) and the card says the reload failed.
        <Card>
          <Space direction="vertical" size={8}>
            <Text type="danger">Could not load the platform feature flags. Please retry.</Text>
            <Button size="small" onClick={() => void flagsQuery.refetch()}>
              Retry
            </Button>
          </Space>
        </Card>
      ) : (
        <>
          {flagsQuery.isError && (
            // A later reload failed: say the values may be out of date instead of looking live.
            <Text type="warning">Could not refresh. Showing the values last loaded.</Text>
          )}
          {flags.map((flag) => (
            <FlagCard
              key={flag.key}
              flag={flag}
              canWrite={canWrite}
              readOnlyReason={readOnlyReason}
              refetchFlags={flagsQuery.refetch}
            />
          ))}
        </>
      )}
    </Space>
  );
};
